<script setup lang="ts">
import { format } from 'date-fns';
import type { FetchContractResponse } from 'src/api/models/fetchContractResponse';
import { FetchContractResponseStatus } from 'src/api/models/fetchContractResponseStatus';
import { FetchContractResponseType } from 'src/api/models/fetchContractResponseType';

const props = defineProps<{
  contracts: FetchContractResponse[];
  loading: boolean;
  page: number;
  totalPages: number;
  totalRecords: number;
  selectedContractId: string | null;
  compact?: boolean;
}>();

const emit = defineEmits<{
  'row-click': [contract: FetchContractResponse];
  'page-change': [page: number];
}>();

function onPageChange(event: { page: number }) {
  emit('page-change', event.page + 1);
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-';
  return format(new Date(dateStr), 'dd-MM-yyyy');
}

function formatUnit(unit?: FetchContractResponse['unit']): string {
  if (!unit) return '-';
  return [unit.fleet_number, unit.manufacture_name, unit.manufactured_year, unit.serial_number]
    .filter(Boolean)
    .join(' - ');
}

function getStatusSeverity(status?: FetchContractResponseStatus): string {
  switch (status) {
    case FetchContractResponseStatus.new:
    case FetchContractResponseStatus.offer_sent:
      return 'info';
    case FetchContractResponseStatus.active:
      return 'success';
    case FetchContractResponseStatus.cancelled:
      return 'danger';
    case FetchContractResponseStatus.offer_expired:
    case FetchContractResponseStatus.ended:
      return 'warning';
    default:
      return 'secondary';
  }
}

function getStatusLabel(status?: FetchContractResponseStatus): string {
  if (!status) return '-';
  return String(status);
}

function getTypeLabel(type?: FetchContractResponseType): string {
  if (!type) return '-';
  return String(type);
}

function getTypeSeverity(type?: FetchContractResponseType): string {
  switch (type) {
    case FetchContractResponseType.care:
      return 'info';
    case FetchContractResponseType.full_care:
      return 'success';
    default:
      return 'secondary';
  }
}

function rowClass(data: FetchContractResponse): string {
  return data.id === props.selectedContractId ? 'bg-primary/5' : '';
}
</script>

<template>
  <div class="flex flex-col">
  <DataTable
    data-testid="contract-list-table"
    :value="contracts"
    :loading="loading"
    scrollable
    selection-mode="single"
    :row-class="rowClass"
    @row-click="emit('row-click', $event.data as FetchContractResponse)"
  >
    <template #empty>
      <div class="text-center py-8 text-color-secondary">Geen contracten gevonden</div>
    </template>

    <Column header="Type" style="width: 110px" data-testid="col-type">
      <template #body="{ data }">
        <Tag :severity="getTypeSeverity(data.type)" :value="getTypeLabel(data.type)" />
      </template>
    </Column>

    <Column header="Contractant" :style="compact ? 'width: 140px' : 'width: 200px'" data-testid="col-customer">
      <template #body="{ data }">{{ data.contract_holder?.name ?? '-' }}</template>
    </Column>

    <Column v-if="!compact" header="Leverancier" style="width: 200px" data-testid="col-supplier">
      <template #body="{ data }">{{ data.operator?.name ?? '-' }}</template>
    </Column>

    <Column header="Unit" :style="compact ? 'width: 180px' : 'width: 240px'" data-testid="col-unit">
      <template #body="{ data }">{{ formatUnit(data.unit) }}</template>
    </Column>

    <Column header="Status" style="width: 100px" data-testid="col-status">
      <template #body="{ data }">
        <Tag :severity="getStatusSeverity(data.status)" :value="getStatusLabel(data.status)" />
      </template>
    </Column>

    <Column header="Startdatum" style="width: 110px" data-testid="col-start-date">
      <template #body="{ data }">{{ formatDate(data.start_date) }}</template>
    </Column>

    <Column v-if="!compact" header="Einddatum" style="width: 110px" data-testid="col-end-date">
      <template #body="{ data }">{{ formatDate(data.end_date) }}</template>
    </Column>

    <Column v-if="!compact" header="Referentie" style="width: 140px" data-testid="col-reference">
      <template #body="{ data }">{{ data.reference_number ?? '-' }}</template>
    </Column>
  </DataTable>

  <Paginator
    data-testid="contract-paginator"
    :rows="15"
    :total-records="totalRecords"
    :first="(page - 1) * 15"
    @page="onPageChange"
  />
  </div>
</template>
